import React, { createContext, useContext, useEffect, useMemo } from 'react';
import useApi from '../hooks/useApi';
import { monitor } from '../api/monitor';
import { useAuth } from './AuthContext';
import { useLive } from './LiveContext';
import { usePrefs } from './PrefsContext';

const DataContext = createContext(null);
export const useData = () => useContext(DataContext);

/** Shared overview snapshot (fields, stations, KPIs, alerts). Refetched on a timer and whenever an alert changes. */
export function DataProvider({ children }) {
  const { user } = useAuth();
  const { alertTick } = useLive();
  const { refresh } = usePrefs();
  const api = useApi(() => monitor.overview(), [], { interval: refresh, enabled: !!user });
  const { reload } = api;
  useEffect(() => { if (alertTick) reload(); }, [alertTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const value = useMemo(() => api, [api.data, api.error, api.loading]); // eslint-disable-line react-hooks/exhaustive-deps
  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

/** Overlay the newest streamed readings on a station summary so numbers move without waiting for a refetch. */
export const withLive = (station, latest) => {
  const r = latest[station.number];
  if (!r) return station;
  const lastAt = station.last?.publishedAt ? new Date(station.last.publishedAt).getTime() : 0;
  if (new Date(r.publishedAt).getTime() < lastAt) return station;
  const { temperature, humidity, pressure, o2, co2, battery, rssi, snr, vpd, dewPoint, publishedAt, node } = r;
  return { ...station, status: station.status === 'offline' ? 'online' : station.status, last: { temperature, humidity, pressure, o2, co2, battery, rssi, snr, vpd, dewPoint, publishedAt, node } };
};

const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);
/** Farm summary recomputed from the live-overlaid stations. */
export const farmStats = (farm, latest) => {
  const stations = farm.stations.map((s) => withLive(s, latest));
  const live = stations.filter((s) => s.last && s.status !== 'offline');
  return {
    stations,
    avgTemp: mean(live.map((s) => s.last.temperature)),
    avgHum: mean(live.map((s) => s.last.humidity)),
    avgVpd: mean(live.map((s) => s.last.vpd)),
    minBattery: live.length ? Math.min(...live.map((s) => s.last.battery)) : null,
  };
};
